/**
 * Reusable stats hover popover (Radix Tooltip).
 * Same appearance as TruncatedDropCell tooltip for consistency.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC, ReactNode } from 'react';
import * as RadixTooltip from '@radix-ui/react-tooltip';

import { cn } from '@/lib/utils';

import { formatDateRangePreset } from './shared/utils/display';

const TOOLTIP_CONTENT_CLASS =
  'z-[200] max-w-sm min-w-[220px] rounded-md bg-foreground px-3 py-2.5 text-background shadow-lg';

/** Labeled section for tooltip content (matches drop tooltip style). */
export const StatsTooltipSection: FC<{
  label: string;
  children: ReactNode;
  valueClassName?: string;
}> = ({ label, children, valueClassName }) => (
  <div>
    <div className="text-[10px] font-semibold uppercase tracking-wider opacity-80">
      {label}
    </div>
    <div className={cn('break-words text-sm', valueClassName)}>{children}</div>
  </div>
);

/** Tags section for stats tooltip (with optional date range grouping). */
export const StatsTooltipTagsBlock: FC<{
  performanceTags?: string[];
  performanceTagDetails?: Array<{ tag: string; dateRangePreset: string }>;
  tagColors?: Record<string, string>;
}> = ({ performanceTags = [], performanceTagDetails = [], tagColors }) => {
  const hasTags =
    (performanceTagDetails?.length ?? performanceTags?.length ?? 0) > 0;
  if (!hasTags) return null;

  const details = performanceTagDetails ?? [];
  const tags = performanceTags ?? [];

  if (details.length === 0) {
    return (
      <>
        <div className="text-[10px] font-semibold uppercase tracking-wider opacity-80">
          Tags
        </div>
        <div className="mt-1 flex flex-wrap gap-x-2 gap-y-1">
          {tags.map((tag) => (
            <span
              key={tag}
              className="rounded px-2 py-0.5 text-[11px] font-medium"
              style={
                tagColors?.[tag]
                  ? { backgroundColor: `${tagColors[tag]}26`, color: tagColors[tag] }
                  : undefined
              }
            >
              {tag}
            </span>
          ))}
        </div>
      </>
    );
  }

  const byRange = new Map<string, string[]>();
  for (const { tag, dateRangePreset } of details) {
    const label = formatDateRangePreset(dateRangePreset);
    const list = byRange.get(label) ?? [];
    if (!list.includes(tag)) list.push(tag);
    byRange.set(label, list);
  }
  const entries = Array.from(byRange.entries());
  const singleRange = entries.length === 1;

  return (
    <>
      <div className="text-[10px] font-semibold uppercase tracking-wider opacity-80">
        {singleRange ? `Tags — ${entries[0]![0]}` : 'Tags'}
      </div>
      <div className="mt-1 flex flex-col gap-1">
        {entries.map(([rangeLabel, rangeTags]) => (
          <div
            key={rangeLabel}
            className="flex flex-wrap items-center gap-x-2 gap-y-0.5"
          >
            {!singleRange && (
              <span className="text-[10px] text-muted-foreground shrink-0">
                {rangeLabel}:
              </span>
            )}
            {rangeTags.map((tag) => (
              <span
                key={tag}
                className="rounded px-2 py-0.5 text-[11px] font-medium"
                style={
                  tagColors?.[tag]
                    ? {
                        backgroundColor: `${tagColors[tag]}26`,
                        color: tagColors[tag],
                      }
                    : undefined
                }
              >
                {tag}
              </span>
            ))}
          </div>
        ))}
      </div>
    </>
  );
};

interface StatsHoverPopoverProps {
  content: ReactNode;
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  sideOffset?: number;
}

export const StatsHoverPopover: FC<StatsHoverPopoverProps> = ({
  content,
  children,
  side = 'top',
  sideOffset = 6,
}) => (
  <RadixTooltip.Provider delayDuration={200} skipDelayDuration={100}>
    <RadixTooltip.Root>
      <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
      <RadixTooltip.Portal>
        <RadixTooltip.Content
          side={side}
          sideOffset={sideOffset}
          avoidCollisions
          collisionPadding={12}
          className={TOOLTIP_CONTENT_CLASS}
        >
          {content}
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>
  </RadixTooltip.Provider>
);
